import { HttpStatusCode, HttpRequestMethodTypes } from 'types';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { FetchOptions } from 'types/fetchWithRetry';
import { getStorageServiceHost, isServerSide } from 'utils/host-config';

import type { StoredCityInsights } from 'utils/city-insights/localize';

/**
 * Fetches the AI-generated city insights (Deep Dive) for a given area slug from
 * the storage service.
 *
 * This mirrors what `/api/city-insights/[slug]` returns to the client, but can
 * be called directly on the server (e.g. from `getServerSideProps`) so the
 * homepage Deep Dive can be rendered from data resolved server-side instead of
 * a client-side fetch.
 *
 * Returns the raw stored payload (base English data plus any per-locale
 * `translations`). Use `selectLocalizedCityInsights` to resolve it to the shape
 * the renderer consumes for a given locale.
 *
 * @param slug The area slug (e.g. "toronto-on").
 * @param host The storage service host. Defaults to `getStorageServiceHost(isServerSide())`.
 * @returns The stored city insights data, or `null` when it does not exist / on error.
 */
export async function getCityInsights(
  slug: string,
  host: string = getStorageServiceHost(isServerSide())
): Promise<StoredCityInsights | null> {
  if (!slug) return null;

  const url = `${host}/v1/city-insights/${encodeURIComponent(slug)}`;
  const options: FetchOptions = { method: HttpRequestMethodTypes.GET };

  try {
    const response = await fetchWithRetry(url, options);

    if (response.status === HttpStatusCode.NOT_FOUND) {
      return null;
    }

    if (!response.ok) {
      console.error(`endpoint=city-insights,url=${url},status=${response?.status},status_text=${response?.statusText}`);
      return null;
    }

    return (await response.json()) as StoredCityInsights;
  } catch (error) {
    console.error(`endpoint=city-insights,url=${url},error=${error}`);
    return null;
  }
}
